import React, { useState, useEffect } from "react";

import classNames from "classnames";
import { Check, Minus, Success, DownMinimalArrow } from "icons";

import { Tab, Typography } from "clearsense-ui/components";
import useForceLightMode from "hooks/useForceLightMode";

import { TIERS, SECTIONS, TAB_STATES, FEATURE_LIST } from "./constants";

import Footer from "../LandingPage/Footer";
import Navigation from "../LandingPage/Navigation";

const PricingPage = ({ user }) => {
  const [tier, setTier] = useState(TAB_STATES.SELF_SERVE);
  const [tierHeader, setTierHeader] = useState(TIERS);

  useEffect(() => {
    tier === TAB_STATES.SELF_SERVE
      ? setTierHeader(TIERS.slice(0, 3))
      : setTierHeader(TIERS.slice(3));
  }, [tier]);

  useForceLightMode();

  return (
    <div className="bg-white">
      <Navigation isLoggedIn={user && true} />
      <div className="py-16 mx-auto bg-white sm:py-24 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center flex-grow ">
          <h1 className="text-5xl font-extrabold text-gray-900 sm:text-center">
            Pricing Plans
          </h1>
          <div className="mt-4 text-xl font-normal text-center text-gray-900">
            Monthly billing
          </div>
        </div>
        <div className="relative w-full mt-8 text-center">
          <Tab className="self-center justify-around w-full">
            <div className="flex gap-4">
              <Tab.Item
                active={tier === TAB_STATES.SELF_SERVE}
                onClick={() => setTier(TAB_STATES.SELF_SERVE)}
                className={classNames(
                  "flex flex-col h-96 border-solid rounded-xl w-96",
                  {
                    "border-2 before:border-transparent border-black shadow-xl ":
                      tier === TAB_STATES.SELF_SERVE,
                  },
                  {
                    "border border-gray-300 shadow-md":
                      tier !== TAB_STATES.SELF_SERVE,
                  }
                )}
              >
                <div className="absolute top-0 left-0 w-full h-full pt-5">
                  <Typography
                    style="h2"
                    type="semibold"
                    className="absolute w-full text-gray-500 whitespace-nowrap"
                  >
                    Self Serve
                  </Typography>
                  <div className="flex items-center mt-16 space-x-1">
                    <div className="w-1/12 h-0 border border-solid" />
                    <Typography
                      style="large"
                      type="bold"
                      className="whitespace-nowrap"
                    >
                      What's included
                    </Typography>
                    <div className="w-7/12 h-0 border border-solid" />
                  </div>
                  <ul className="px-8 mt-8 space-y-3 text-left">
                    {FEATURE_LIST.selfServe.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-2 flex-nowrap"
                      >
                        <Success className="w-5 h-5 text-green-500" />
                        <Typography style="large">{feature}</Typography>
                      </li>
                    ))}
                  </ul>
                  {tier === TAB_STATES.SELF_SERVE && (
                    <div className="flex justify-center w-full">
                      <DownMinimalArrow size={80} className="w-10 h-10" />
                    </div>
                  )}
                </div>
              </Tab.Item>
              <Tab.Item
                active={tier === TAB_STATES.MANAGED}
                onClick={() => setTier(TAB_STATES.MANAGED)}
                className={classNames(
                  "flex flex-col h-96 border-solid rounded-xl w-96",
                  {
                    "border-2 before:border-transparent border-black shadow-xl":
                      tier === TAB_STATES.MANAGED,
                  },
                  {
                    "border border-gray-300 shadow-md":
                      tier !== TAB_STATES.MANAGED,
                  }
                )}
              >
                <div className="absolute top-0 left-0 w-full h-full pt-5">
                  <Typography
                    style="h2"
                    type="semibold"
                    className="absolute w-full text-gray-500 whitespace-nowrap"
                  >
                    Managed Services
                  </Typography>
                  <div className="flex items-center mt-16 space-x-1">
                    <div className="w-1/12 h-0 border border-solid" />
                    <Typography
                      style="large"
                      type="bold"
                      className="whitespace-nowrap"
                    >
                      What's included
                    </Typography>
                    <div className="w-7/12 h-0 border border-solid" />
                  </div>
                  <Typography
                    style="large"
                    type="bold"
                    className="mx-8 mt-4 text-left"
                  >
                    Everything from Self Serve +
                  </Typography>
                  <ul className="px-8 mt-6 space-y-3 text-left ">
                    {FEATURE_LIST.managedServices.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-2 flex-nowrap"
                      >
                        <Success className="w-5 h-5 text-green-500" />
                        <Typography style="large">{feature}</Typography>
                      </li>
                    ))}
                  </ul>
                  {tier === TAB_STATES.MANAGED && (
                    <div className="flex justify-center w-full mt-9">
                      <DownMinimalArrow size={80} className="w-10 h-10" />
                    </div>
                  )}
                </div>
              </Tab.Item>
            </div>
          </Tab>
        </div>
        <div className="py-10 mt-16 overflow-x-auto border">
          <table className="w-full">
            <thead>
              <tr>
                <th
                  className="w-1/4 px-6 pb-10 text-lg font-medium text-center text-gray-900"
                  scope="col"
                >
                  <span>Plans</span>
                </th>
                {tierHeader.map(tier => (
                  <th
                    key={tier.name}
                    className="w-1/6 px-6 pb-10 text-lg font-medium leading-6 text-left text-gray-900"
                    scope="col"
                  >
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="border-t border-gray-200 divide-y divide-gray-200">
              {SECTIONS.map(section => (
                <React.Fragment key={section.name}>
                  {section.features.map(feature => (
                    <tr key={feature.name}>
                      <th
                        className={classNames(
                          {
                            "py-3 pl-6 text-sm font-medium text-center text-gray-900 bg-gray-50":
                              feature.priceInfo,
                          },
                          {
                            "w-20 p-3 text-sm font-normal text-center text-gray-500":
                              !feature.priceInfo,
                          }
                        )}
                        scope="row"
                      >
                        {feature.name}
                      </th>
                      {tierHeader.map(tier => (
                        <td
                          key={tier.name}
                          className={classNames("px-6 py-5", {
                            "py-3 pl-6 text-sm font-medium text-left text-gray-900 bg-gray-50":
                              feature.priceInfo,
                          })}
                        >
                          {feature.priceInfo ? (
                            <p>
                              {feature.tiers[tier.name] ? (
                                <span className="text-3xl font-extrabold text-gray-900">
                                  {feature.tiers[tier.name]}
                                </span>
                              ) : (
                                <Minus
                                  className="w-5 h-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              )}
                            </p>
                          ) : typeof feature.tiers[tier.name] === "string" ? (
                            <span className="block text-sm text-gray-700">
                              {feature.tiers[tier.name]}
                            </span>
                          ) : (
                            <>
                              {feature.tiers[tier.name] === true ? (
                                <Check
                                  className="w-5 h-5 text-green-500 "
                                  aria-hidden="true"
                                />
                              ) : (
                                <Minus
                                  className="w-5 h-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              )}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <th scope="row">Choose your plan</th>
                {tierHeader.map(tier => (
                  <td key={tier.name} className="px-6 pt-5">
                    {user ? (
                      user.plan === tier.name ? (
                        <span className="block w-full py-2 text-sm font-semibold text-center text-white bg-green-500 rounded-md hover:bg-green-900">
                          {tier.name} Plan Active
                        </span>
                      ) : (
                        <a
                          href={tier.href(
                            user.authentication_token,
                            user.email,
                            tier.name,
                            tier.interval
                          )}
                          className="block w-full py-2 text-sm font-semibold text-center text-white bg-blue-600 rounded-md disabled"
                        >
                          {tier.name === "Free" ? "Try for " : "Buy "}
                          {tier.name}
                        </a>
                      )
                    ) : (
                      <span className="block w-full py-2 text-sm font-semibold text-center text-white bg-blue-600 rounded-md hover:bg-gray-900">
                        {tier.name === "Free" ? "Try for " : "Buy "}
                        {tier.name}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PricingPage;
